import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, CreditCard, Shield } from "lucide-react";
import { API } from "../App";

/**
 * Customer lands here from the "Pay Deposit Now" button in the manual-booking
 * deposit-request email.
 * URL: /pay-deposit/:bookingId
 *
 * Flow:
 *   1. GET /api/payments/deposit-summary/{booking_id} to show what's owed
 *   2. Click "Pay Deposit" → POST /api/payments/create-deposit-for-booking/{booking_id}
 *   3. Redirect to Revolut checkout_url
 *
 * Mirrors PayBalancePage.jsx exactly for consistency.
 */
const PayDepositPage = () => {
  const { bookingId } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!bookingId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/payments/deposit-summary/${bookingId}`);
        setSummary(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "We couldn't find that booking.");
      } finally {
        setLoading(false);
      }
    })();
  }, [bookingId]);

  const startPayment = async () => {
    setProcessing(true);
    setError("");
    try {
      const r = await axios.post(`${API}/payments/create-deposit-for-booking/${bookingId}`);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start payment. Please try again.");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-saturn-orange animate-spin" />
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 pb-28 sm:pb-4">
        <div
          className="max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-8 text-center"
          data-testid="pay-deposit-not-found"
        >
          <XCircle className="w-14 h-14 text-red-400 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Booking not found</h1>
          <p className="text-slate-400">This payment link is no longer valid — please contact us for a fresh link.</p>
        </div>
      </div>
    );
  }

  if (summary?.deposit_paid) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 pb-28 sm:pb-4">
        <div
          className="max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-8 text-center"
          data-testid="pay-deposit-already-paid"
        >
          <CheckCircle2 className="w-14 h-14 text-green-400 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Deposit already paid</h1>
          <p className="text-slate-400 mb-4">
            Your booking is confirmed — we'll see you on the day.
          </p>
          <p className="text-slate-500 text-sm font-mono">{bookingId}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4 pb-28 sm:pb-4">
      <div
        className="max-w-lg w-full bg-slate-900/85 border border-slate-700 rounded-2xl shadow-2xl p-8"
        data-testid="pay-deposit-page"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-saturn-orange/20 flex items-center justify-center mx-auto mb-3">
            <CreditCard className="w-8 h-8 text-saturn-orange" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1">Pay your deposit</h1>
          <p className="text-slate-400 text-sm">
            Booking <span className="font-mono text-slate-300">{bookingId}</span>
          </p>
        </div>

        <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-5 mb-5">
          <div className="flex items-center justify-between text-slate-300 mb-2 text-sm">
            <span>Customer</span>
            <span className="text-white font-medium">{summary?.customer_name || "—"}</span>
          </div>
          {summary?.vehicle_reg && (
            <div className="flex items-center justify-between text-slate-300 mb-2 text-sm">
              <span>Vehicle Reg</span>
              <span className="text-white font-mono">{summary.vehicle_reg}</span>
            </div>
          )}
          {summary?.booking_date && (
            <div className="flex items-center justify-between text-slate-300 mb-2 text-sm">
              <span>Appointment</span>
              <span className="text-white font-medium">
                {summary.booking_date}{summary.booking_time ? ` · ${summary.booking_time}` : ""}
              </span>
            </div>
          )}
          <hr className="border-slate-700 my-3" />
          <div className="flex items-center justify-between text-slate-300 mb-1 text-sm">
            <span>Booking total</span>
            <span className="text-white font-medium">€{Number(summary?.total || 0).toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between text-slate-300 mb-1 text-sm">
            <span>Balance due on collection</span>
            <span className="text-slate-300">€{Number(summary?.balance_after_deposit || 0).toFixed(2)}</span>
          </div>
          <hr className="border-slate-700 my-3" />
          <div className="flex items-center justify-between text-lg">
            <span className="text-slate-300 font-medium">Deposit now</span>
            <span className="text-saturn-orange font-bold text-2xl" data-testid="deposit-amount">
              €{Number(summary?.deposit_amount || 0).toFixed(2)}
            </span>
          </div>
        </div>

        {error && (
          <div
            className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg p-3 mb-4 text-sm"
            data-testid="pay-deposit-error"
          >
            {error}
          </div>
        )}

        <button
          onClick={startPayment}
          disabled={processing || (summary?.deposit_amount ?? 0) <= 0}
          className="w-full bg-saturn-orange hover:bg-saturn-orange/80 disabled:opacity-50 text-white py-4 rounded-lg font-semibold transition flex items-center justify-center gap-2"
          data-testid="pay-deposit-btn"
        >
          {processing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Preparing checkout…
            </>
          ) : (
            <>
              Pay €{Number(summary?.deposit_amount || 0).toFixed(2)} securely with Revolut
            </>
          )}
        </button>

        <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500">
          <Shield className="w-4 h-4" />
          <span>Secure payment · Your booking is confirmed once payment clears</span>
        </div>
      </div>
    </div>
  );
};

export default PayDepositPage;
